import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  getInstrumentOptions,
  getProductOptions,
  getVenueAssetOptions,
  getVenueOptions,
} from '#/unified-rfqs/ui/rfq-filters/form-view';
import { getActiveProductsByVenue } from '#/products/domain';
import { FormOption } from '#/unified-rfqs/ui/rfq-builder/types';
import { Source } from '#/unified-rfqs/entities/products/venue';
import { useAccounts } from '#/unified-rfqs/contexts/accounts';

export interface FiltersView {
  readonly activeFiltersCount: number;
  readonly venues: string[];
  readonly assets: string[];
  readonly products: string[];
  readonly instruments: string[];
  readonly accounts: string[];
  readonly venueOptions: ReadonlyArray<FormOption>;
  readonly assetOptions: ReadonlyArray<FormOption>;
  readonly productOptions: ReadonlyArray<FormOption>;
  readonly instrumentOptions: ReadonlyArray<FormOption>;
  readonly accountOptions: ReadonlyArray<FormOption>;
}

export interface FiltersState {
  readonly venues: string[];
  readonly assets: string[];
  readonly products: string[];
  readonly instruments: string[];
  readonly accounts: string[];
}

interface FiltersActions {
  readonly setValues: (values: Partial<FiltersState>) => void;
  readonly reset: () => void;
}

const INITIAL_STATE: FiltersState = {
  venues: [],
  assets: [],
  products: [],
  instruments: [],
  accounts: [],
};

const [useViewCtx, ViewCtxProvider] = createSafeContext<FiltersView>(
  'PositionFiltersView',
);

const [useActionsCtx, ActionsCtxProvider] = createSafeContext<FiltersActions>(
  'PositionFiltersActions',
);

const [useStateCtx, StateCtxProvider] = createSafeContext<FiltersState>(
  'PositionFiltersState',
);

const ACTIVE_VENUE = 'DBT';
const ACTIVE_PRODUCTS = getActiveProductsByVenue(ACTIVE_VENUE);
const VENUE_OPTIONS = getVenueOptions().filter(
  (option) => option.value === ACTIVE_VENUE,
);
const ASSET_OPTIONS = getVenueAssetOptions(ACTIVE_PRODUCTS);
const PRODUCT_OPTIONS = getProductOptions(ACTIVE_PRODUCTS);
const INSTRUMENT_OPTIONS = getInstrumentOptions();

export const usePositionFiltersView = useViewCtx;
export const usePositionFiltersActions = useActionsCtx;
export const usePositionFiltersState = useStateCtx;

export default function PositionFiltersProvider({
  children,
}: PropsWithChildren) {
  const [state, setState] = useState<FiltersState>(INITIAL_STATE);
  const view = useCreateView(state);
  const actions = useMemo(() => createActions(setState), []);

  return (
    <StateCtxProvider value={state}>
      <ViewCtxProvider value={view}>
        <ActionsCtxProvider value={actions}>{children}</ActionsCtxProvider>
      </ViewCtxProvider>
    </StateCtxProvider>
  );
}

function activeFiltersCountView(state: FiltersState): number {
  const filters = Object.values(state) as string[][];
  return filters.filter((arr) => arr.length > 0).length;
}

function useCreateView(state: FiltersState): FiltersView {
  const accountOptions = useAccountOptions();
  return useMemo(
    () => ({
      ...state,
      activeFiltersCount: activeFiltersCountView(state),
      venueOptions: VENUE_OPTIONS,
      assetOptions: ASSET_OPTIONS,
      productOptions: PRODUCT_OPTIONS,
      instrumentOptions: INSTRUMENT_OPTIONS,
      accountOptions,
    }),
    [accountOptions, state],
  );
}

function createActions(
  setState: Dispatch<SetStateAction<FiltersState>>,
): FiltersActions {
  return {
    reset: () => setState(INITIAL_STATE),
    setValues: (values) =>
      setState((state) => ({
        ...state,
        ...values,
      })),
  };
}

function useAccountOptions(venue: Source = 'DBT'): ReadonlyArray<FormOption> {
  const { selectByVenue } = useAccounts();
  const accounts = selectByVenue(venue);
  return accounts.map((account) => ({
    label: account.name,
    value: account.name,
  }));
}
